import { useEffect } from "react";
import { useWeb3React } from "@web3-react/core";
import { useToast, Flex, Text, Button, CloseButton } from "@chakra-ui/react";

// Utils
import { getConnector } from "utils/connectors";

// Services
import { switchToMainnet } from "services/common";

// Types
import { NetworkConfigType } from "config/network/types";

const useInactiveListener = (
  account: string | null | undefined,
  provider: any,
  connectorPref: string,
  enable = false,
  config: NetworkConfigType,
  setSelectedChain: (ch: string | number) => void,
  clearWalletData: any
) => {
  const { activate, deactivate } = useWeb3React();

  const toast = useToast();

  useEffect((): any => {
    const connector = getConnector(connectorPref, config);

    if (account && enable) {
      const handleConnect = () => {
        activate(connector);
      };

      const handleChainChanged = (chainId: string | number) => {
        const supportedChains = config.supportedChainIDs;

        if (supportedChains.includes(+chainId) && config.chainId !== chainId) {
          setSelectedChain(chainId);

          setTimeout(() => activate(connector, undefined, true), 1000);
        } else if (!supportedChains.includes(+chainId)) {
          deactivate();
          toast({
            duration: 10000,
            position: "top",
            containerStyle: {
              width: "700px",
              maxWidth: "100%",
            },
            render: ({ onClose }) => (
              <Flex
                color="white"
                p={3}
                bg="#D9534F"
                borderRadius="0.5rem"
                alignItems="center"
                gap="1rem"
              >
                <Flex
                  flexGrow={1}
                  gap="1rem"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Text as="span">
                    Matic staking is supported on Ethereum and Polygon Network.
                  </Text>

                  <Button
                    bg="white"
                    color="black"
                    size="sm"
                    _focus={{
                      bg: "brand.primary",
                      color: "white",
                    }}
                    _hover={{
                      bg: "brand.primary",
                      color: "white",
                    }}
                    onClick={() => {
                      switchToMainnet(
                        connectorPref,
                        provider,
                        config.chainId,
                        () => activate(connector, undefined, true)
                      );
                      onClose();
                    }}
                  >
                    Switch Network
                  </Button>
                </Flex>
                <CloseButton onClick={onClose} />
              </Flex>
            ),
          });
        }
      };

      const handleAccountsChanged = (accounts: string[]) => {
        clearWalletData();

        const act = accounts[0];

        if (accounts.length > 0 && account !== act) {
          setTimeout(() => activate(connector, undefined, true), 1000);
        }
      };

      provider.on("connect", handleConnect);
      provider.on("chainChanged", handleChainChanged);
      provider.on("accountsChanged", handleAccountsChanged);

      return () => {
        if (provider.removeListener) {
          provider.removeListener("connect", handleConnect);
          provider.removeListener("chainChanged", handleChainChanged);
          provider.removeListener("accountsChanged", handleAccountsChanged);
        }
      };
    }
  }, [enable, activate, provider]);
};

export default useInactiveListener;
